/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import type { Logger } from '../utils/logger';
import type { Body, IRequestOpts, QueryDict } from './interface';
import type { Method } from './method';

/**
 * Represents a failed request that should be retried after token refresh
 */
export type QueuedRequest<T = unknown> = {
  id: string;
  method: Method;
  path: string;
  queryParams?: QueryDict;
  body?: Body;
  opts?: IRequestOpts;
  resolve: (value: T) => void;
  reject: (error: Error) => void;
  timestamp: number;
  retryCount: number;
};

/**
 * Configuration for the request queue
 */
export type RequestQueueConfig = {
  /** Maximum number of requests to queue */
  maxQueueSize?: number;
  /** Maximum time (ms) a request can stay in queue before being rejected */
  maxQueueTime?: number;
  /** Maximum retry attempts per request */
  maxRetries?: number;
  /** Logger instance */
  logger?: Logger;
};

/**
 * Manages a queue of failed requests that should be retried after token refresh.
 * This allows the SDK to automatically retry API calls that failed due to token expiration.
 */
export class RequestQueue {
  private queue = new Map<string, QueuedRequest>();
  private nextId = 0;
  private readonly config: RequestQueueConfig & { maxQueueSize: number; maxQueueTime: number; maxRetries: number };

  public constructor(config: RequestQueueConfig = {}) {
    this.config = {
      maxQueueSize: config.maxQueueSize ?? 50,
      maxQueueTime: config.maxQueueTime ?? 5 * 60 * 1000, // 5 minutes
      maxRetries: config.maxRetries ?? 3,
      logger: config.logger,
    };
  }

  /**
   * Add a failed request to the queue for retry
   * @returns Promise that resolves when the request is retried successfully
   */
  public enqueue<T>(
    method: Method,
    path: string,
    queryParams?: QueryDict,
    body?: Body,
    opts?: IRequestOpts,
  ): Promise<T> {
    // Check queue size limit
    if (this.queue.size >= this.config.maxQueueSize) {
      this.config.logger?.warn(`Request queue is full (${this.config.maxQueueSize}), rejecting request to ${path}`);
      return Promise.reject(new Error('Request queue is full. Token refresh taking too long.'));
    }

    const id = `req_${++this.nextId}_${Date.now()}`;

    return new Promise<T>((resolve, reject) => {
      const queuedRequest: QueuedRequest<T> = {
        id,
        method,
        path,
        queryParams,
        body,
        opts,
        resolve,
        reject,
        timestamp: Date.now(),
        retryCount: 0,
      };

      this.queue.set(id, queuedRequest as QueuedRequest);
      this.config.logger?.debug(`Queued request ${id}: ${method} ${path}`);
    });
  }

  /**
   * Get all queued requests
   */
  public getAll(): QueuedRequest[] {
    return Array.from(this.queue.values());
  }

  /**
   * Get number of queued requests
   */
  public size(): number {
    return this.queue.size;
  }

  /**
   * Remove a request from the queue
   */
  public remove(id: string): boolean {
    return this.queue.delete(id);
  }

  /**
   * Clear all queued requests and reject them with an error
   */
  public clear(reason = 'Request queue cleared'): void {
    const error = new Error(reason);
    for (const request of this.queue.values()) {
      request.reject(error);
    }
    this.queue.clear();
    this.config.logger?.debug('Request queue cleared');
  }

  /**
   * Clean up expired requests from the queue
   */
  public cleanExpired(): number {
    const now = Date.now();
    let cleanedCount = 0;

    for (const [id, request] of this.queue.entries()) {
      const age = now - request.timestamp;
      if (age > this.config.maxQueueTime) {
        request.reject(new Error(`Request timed out in queue after ${Math.round(age / 1000)}s`));
        this.queue.delete(id);
        cleanedCount++;
      }
    }

    if (cleanedCount > 0) {
      this.config.logger?.debug(`Cleaned ${cleanedCount} expired requests from queue`);
    }

    return cleanedCount;
  }

  /**
   * Check if a request can be retried based on retry count
   */
  public canRetry(request: QueuedRequest): boolean {
    return request.retryCount < this.config.maxRetries;
  }

  /**
   * Increment retry count for a request
   */
  public incrementRetryCount(id: string): void {
    const request = this.queue.get(id);
    if (request) {
      request.retryCount++;
    }
  }

  /**
   * Get queue statistics
   */
  public getStats(): {
    size: number;
    oldestRequest: number | null;
    avgAge: number;
  } {
    if (this.queue.size === 0) {
      return { size: 0, oldestRequest: null, avgAge: 0 };
    }

    const now = Date.now();
    let totalAge = 0;
    let oldestAge = 0;

    for (const request of this.queue.values()) {
      const age = now - request.timestamp;
      totalAge += age;
      if (age > oldestAge) {
        oldestAge = age;
      }
    }

    return {
      size: this.queue.size,
      oldestRequest: oldestAge,
      avgAge: Math.round(totalAge / this.queue.size),
    };
  }
}
